// REST contracts (docs/project.md §7–§8, docs/db.md §8). The API validates requests with these
// schemas; the PWA types its fetches with them. Names: XxxRequest / XxxResponse.
import { z } from 'zod';
import { ExampleListSchema, PartOfSpeechSchema, RelatedListSchema, TranslationListSchema, } from './collections.js';
import { LOGIN_CODE_LENGTH, UI_LOCALES } from './constants.js';
import { DictionaryRowSchema, DictionarySummarySchema, EntryRowSchema, GlossRowSchema, LangCodeSchema, LexemeRowSchema, SessionRowSchema, ShareRowSchema, ShareStatusSchema, UserRowSchema, UuidSchema, } from './rows.js';
export const EmailSchema = z.string().trim().toLowerCase().email().max(254);
export const UiLocaleSchema = z.enum(UI_LOCALES);
export const HeadwordSchema = z.string().trim().min(1).max(200);
export const NameSchema = z.string().trim().min(1).max(80);
export const OkSchema = z.object({ ok: z.literal(true) });
// ---- errors ----------------------------------------------------------------------------
export const API_ERROR_CODES = [
    'unauthorized',
    'forbidden',
    'not_found',
    'validation',
    'rate_limited',
    'ai_unavailable',
    'already_in_dictionary',
    'invalid_code',
    'code_expired',
    'share_invalid',
    'conflict',
    'internal',
];
export const ApiErrorSchema = z.object({
    error: z.object({ code: z.enum(API_ERROR_CODES), message: z.string() }),
});
// ---- auth ------------------------------------------------------------------------------
export const AuthCodeRequestSchema = z.object({ email: EmailSchema });
export const AuthCodeResponseSchema = OkSchema;
export const AuthVerifyRequestSchema = z.object({
    email: EmailSchema,
    code: z
        .string()
        .trim()
        .regex(new RegExp(`^\\d{${LOGIN_CODE_LENGTH}}$`)),
    // present when the sign-in started from a teacher invitation link (docs/project.md §4)
    shareToken: z.string().min(1).optional(),
});
export const AuthVerifyResponseSchema = z.object({
    user: UserRowSchema,
    isNew: z.boolean(),
    onboardingRequired: z.boolean(),
});
export const AuthLogoutResponseSchema = OkSchema;
// ---- me --------------------------------------------------------------------------------
export const MeResponseSchema = z.object({ user: UserRowSchema });
export const MePatchRequestSchema = z
    .object({
    name: NameSchema.optional(),
    locale: UiLocaleSchema.optional(),
    nativeLang: LangCodeSchema.optional(),
})
    .strict();
export const MePatchResponseSchema = MeResponseSchema;
export const OnboardingRequestSchema = z.object({
    name: NameSchema,
    nativeLang: LangCodeSchema,
    targetLang: LangCodeSchema,
    locale: UiLocaleSchema,
});
export const OnboardingResponseSchema = z.object({
    user: UserRowSchema,
    dictionary: DictionarySummarySchema,
});
export const SessionsResponseSchema = z.object({ sessions: z.array(SessionRowSchema) });
export const SessionDeleteResponseSchema = OkSchema;
// ---- dictionaries ----------------------------------------------------------------------
export const DictionariesResponseSchema = z.object({
    dictionaries: z.array(DictionarySummarySchema),
});
export const DictionaryCreateRequestSchema = z.object({
    targetLang: LangCodeSchema,
    baseLang: LangCodeSchema,
});
export const DictionaryCreateResponseSchema = z.object({ dictionary: DictionarySummarySchema });
export const SharesResponseSchema = z.object({ shares: z.array(ShareRowSchema) });
export const ShareCreateRequestSchema = z.object({ email: EmailSchema });
export const ShareCreateResponseSchema = z.object({ share: ShareRowSchema });
export const ShareRevokeResponseSchema = OkSchema;
export const DictionaryLeaveResponseSchema = OkSchema;
// GET /shares/:token — what the invited teacher sees before signing in / accepting
export const ShareLookupResponseSchema = z.object({
    dictionary: z.object({
        id: UuidSchema,
        targetLang: LangCodeSchema,
        baseLang: LangCodeSchema,
    }),
    ownerName: z.string().nullable(),
    email: z.string(),
    status: ShareStatusSchema,
});
export const ShareAcceptResponseSchema = z.object({ dictionary: DictionarySummarySchema });
// ---- entries ---------------------------------------------------------------------------
export const EntryCreateRequestSchema = z.object({ headword: HeadwordSchema });
// The AI-free form: what the user typed goes into entry overrides (docs/project.md §7)
export const EntryManualCreateRequestSchema = z.object({
    headword: HeadwordSchema,
    partOfSpeech: PartOfSpeechSchema.optional(),
    transcription: z.string().trim().max(200).optional(),
    translations: z
        .array(z.object({
        text: z.string().trim().min(1).max(500),
        contextLabel: z.string().trim().max(100).optional(),
    }))
        .max(50),
    examples: z
        .array(z.object({
        text: z.string().trim().min(1).max(1000),
        translation: z.string().trim().max(1000),
    }))
        .max(50),
});
export const EntryCreateResponseSchema = z.object({
    entry: EntryRowSchema,
    lexeme: LexemeRowSchema,
    gloss: GlossRowSchema.nullable(),
    // false when the word was already in the dictionary (retry, second device, undelete)
    created: z.boolean(),
});
// PUT /entries/:id — the whole row of overrides; null = inherit the shared value
export const EntryUpdateRequestSchema = z.object({
    transcription: z.string().trim().max(200).nullable(),
    partOfSpeech: PartOfSpeechSchema.nullable(),
    translations: TranslationListSchema.nullable(),
    examples: ExampleListSchema.nullable(),
    related: RelatedListSchema.nullable(),
});
export const EntryUpdateResponseSchema = z.object({ entry: EntryRowSchema });
export const EntryHeadwordRequestSchema = z.object({ headword: HeadwordSchema });
export const EntryHeadwordResponseSchema = z.object({
    entry: EntryRowSchema,
    lexeme: LexemeRowSchema,
    gloss: GlossRowSchema.nullable(),
});
// POST /entries/:id/fill — build the card again after a failed fill: the rows with the failure
// cleared, the worker delivers the result through sync
export const EntryFillResponseSchema = z.object({
    lexeme: LexemeRowSchema,
    gloss: GlossRowSchema.nullable(),
});
// GET /dictionaries/:id/lookup?headword= — a word of the shared base as this dictionary sees it:
// the card behind a synonym or antonym tapped in another card, before it is added. Read-only;
// `entryId` is the dictionary's live entry when the word is already in it (docs/project.md §7).
export const LookupQuerySchema = z.object({ headword: HeadwordSchema });
export const LookupResponseSchema = z.object({
    lexeme: LexemeRowSchema.nullable(),
    gloss: GlossRowSchema.nullable(),
    entryId: UuidSchema.nullable(),
});
// GET /dictionaries/:id/suggest?q= — words of the shared base for the omnibox, beside the local
// search: built cards with a translation for this dictionary's pair, not in the dictionary yet,
// whose spelling starts with the query (docs/project.md §7). Read-only.
export const SuggestQuerySchema = z.object({ q: HeadwordSchema });
export const SuggestionSchema = z.object({
    lexemeId: UuidSchema,
    headword: z.string(),
    translations: TranslationListSchema,
});
export const SuggestResponseSchema = z.object({
    // the word the add request would link the query to (the same spelling, or the only one
    // without diacritics, docs/db.md §3), when it is one of the suggestions; not repeated below
    match: SuggestionSchema.nullable(),
    // the rest, the exact spelling first, then shorter words first
    suggestions: z.array(SuggestionSchema),
});
export const EntryDeleteResponseSchema = z.object({ entry: EntryRowSchema });
export const ViewsRequestSchema = z.object({
    views: z
        .array(z.object({ entryId: UuidSchema, viewedAt: z.string().datetime({ offset: true }) }))
        .min(1)
        .max(1000),
});
export const ViewsResponseSchema = OkSchema;
// ---- subtitle translation (the browser extension, docs/project.md §7) ------------------
// A line of up to a few sentences; the model answers in the dictionary's base language
export const TranslateRequestSchema = z.object({ text: z.string().trim().min(1).max(500) });
// The API never calls the model: a line not translated yet is queued and the client asks again
export const TranslateResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('ready'), translation: z.string() }),
    z.object({ status: z.literal('pending') }),
    z.object({ status: z.literal('failed') }),
]);
// ---- sync ------------------------------------------------------------------------------
export const SyncQuerySchema = z.object({
    // bigint cursor as a string; "0" for the first load
    since: z.string().regex(/^\d+$/).default('0'),
});
export const SyncResponseSchema = z.object({
    entries: z.array(EntryRowSchema),
    lexemes: z.array(LexemeRowSchema),
    glosses: z.array(GlossRowSchema),
    cursor: z.string(),
    more: z.boolean(),
});
// SSE `GET /sync/events`: event name "dictionary", data = this payload
export const SyncEventSchema = z.object({ dictionaryId: UuidSchema });
export const SYNC_EVENT_NAME = 'dictionary';
export const HealthResponseSchema = z.object({ status: z.literal('ok') });
// Re-exported so API consumers import everything from one place
export { DictionaryRowSchema };
//# sourceMappingURL=api.js.map